import { useCallback } from 'react';
import { sdk } from '@/sdk';
import useSWR from 'swr';
import { Associate } from '@/types/entity/associate';
import { Address } from '@shared/types/account';
import { useMutationLock } from '@/providers/mutation-lock';
import useAccount from '../useAccount';

const useBusinessUnits = () => {
  const { loggedIn } = useAccount();

  // EWM-521 — business-unit mutations participate in the site-wide lock.
  const { withLock } = useMutationLock();

  const {
    data,
    mutate: mutateBusinessUnits,
    isLoading,
    error: swrError,
  } = useSWR(!loggedIn ? null : '/action/business-unit/getBusinessUnits', () =>
    sdk.composableCommerce.businessUnit.getBusinessUnits({ expandStores: true }),
  );

  const addBusinessUnit = useCallback(
    (payload: {
      account: {
        accountId: string;
        email: string;
        companyName: string;
      };
      storeId: string;
    }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.createBusinessUnit({
          account: payload.account,
        });

        mutateBusinessUnits();

        return response.isError ? {} : response.data;
      }),
    [withLock, mutateBusinessUnits],
  );

  const updateBusinessUnit = useCallback(
    (payload: { name: string; contactEmail: string; businessUnitKey: string }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.updateBusinessUnit(
          {
            name: payload.name,
            contactEmail: payload.contactEmail,
          },
          { businessUnitKey: payload.businessUnitKey },
        );

        mutateBusinessUnits();

        return response.isError ? {} : response.data;
      }),
    [withLock, mutateBusinessUnits],
  );

  const addAssociate = useCallback(
    ({
      email,
      roles,
      businessUnitKey,
      firstName,
      lastName,
    }: Partial<Associate> & { businessUnitKey: string; firstName?: string; lastName?: string }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.addAssociate(
          { email: email as string, roleKeys: roles ?? [], firstName, lastName },
          { businessUnitKey },
        );

        mutateBusinessUnits();

        // EWM-179 — return structured failure info so callers can distinguish
        // `account_email_duplicated_error` (cross-BU duplicate) from generic failure.
        if (response.isError) {
          // The SDK fetcher parses the response body JSON and spreads all its
          // properties onto the ActionError instance (via FetchError constructor).
          // Backend handleError emits { statusCode, message, errorName? }, so
          // errorName lives directly on response.error — not on tracingData.
          const errorName = (response.error as unknown as { errorName?: string })?.errorName;
          return { _error: true, errorName } as const;
        }
        return response.data;
      }),
    [withLock, mutateBusinessUnits],
  );

  const updateAssociate = useCallback(
    ({ id, roles, businessUnitKey }: Partial<Associate> & { businessUnitKey: string }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.updateAssociate(
          { accountId: id as string, roleKeys: roles ?? [] },
          { businessUnitKey },
        );

        mutateBusinessUnits();

        return response.isError ? {} : response.data;
      }),
    [withLock, mutateBusinessUnits],
  );

  const resendAssociateInvitation = useCallback(
    ({ id, businessUnitKey }: { id: string; businessUnitKey: string }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.resendAssociateInvitation(
          { accountId: id },
          { businessUnitKey },
        );

        if (response.isError) {
          const errorName = (response.error as unknown as { errorName?: string })?.errorName;
          return { _error: true, errorName } as const;
        }
        return response.data;
      }),
    [withLock],
  );

  const deactivateAssociate = useCallback(
    ({ id, businessUnitKey }: { id: string; businessUnitKey: string }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.deactivateAssociate(
          { accountId: id },
          { businessUnitKey },
        );

        mutateBusinessUnits();

        if (response.isError) {
          const errorName = (response.error as unknown as { errorName?: string })?.errorName;
          return { _error: true, errorName } as const;
        }
        return response.data;
      }),
    [withLock, mutateBusinessUnits],
  );

  const reactivateAssociate = useCallback(
    ({ id, businessUnitKey }: { id: string; businessUnitKey: string }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.reactivateAssociate(
          { accountId: id },
          { businessUnitKey },
        );

        mutateBusinessUnits();

        if (response.isError) {
          const errorName = (response.error as unknown as { errorName?: string })?.errorName;
          return { _error: true, errorName } as const;
        }
        return response.data;
      }),
    [withLock, mutateBusinessUnits],
  );

  const addAddress = useCallback(
    ({ businessUnitKey, ...address }: Address & { businessUnitKey: string }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.addAddress({ address }, { businessUnitKey });

        mutateBusinessUnits();

        return response.isError ? {} : response.data;
      }),
    [withLock, mutateBusinessUnits],
  );

  const updateAddress = useCallback(
    ({ businessUnitKey, ...address }: Address & { businessUnitKey: string }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.updateAddress({ address }, { businessUnitKey });

        mutateBusinessUnits();

        return response.isError ? {} : response.data;
      }),
    [withLock, mutateBusinessUnits],
  );

  const removeAddress = useCallback(
    ({ businessUnitKey, addressId }: { addressId: string; businessUnitKey: string }) =>
      withLock(async () => {
        const response = await sdk.composableCommerce.businessUnit.removeAddress({ addressId }, { businessUnitKey });

        mutateBusinessUnits();

        return response.isError ? {} : response.data;
      }),
    [withLock, mutateBusinessUnits],
  );

  return {
    businessUnits: data?.isError ? [] : (data?.data ?? []),
    defaultBusinessUnit: data?.isError ? undefined : data?.data?.[0],
    // EWM-179 — surface SWR state so consumers (e.g. useContactsTabData)
    // can render real loading / error UI instead of hardcoded false / null.
    isLoading,
    error: swrError ?? (data?.isError ? new Error('Failed to load business units') : null),
    addBusinessUnit,
    updateBusinessUnit,
    addAssociate,
    updateAssociate,
    resendAssociateInvitation,
    deactivateAssociate,
    reactivateAssociate,
    addAddress,
    updateAddress,
    removeAddress,
  };
};

export default useBusinessUnits;
